import { useCallback, useRef } from 'react';
import { AmlCallView } from '../../phone-calls/types';
import { AML_UNCERTAINTY_CIRCLE_COLOR, FEATURE_LAYERS_PROPERTIES } from '../constants';
import { Graphic, WebMap } from '../constructors';
import { getAmlCallLastAmlLocationGeoPointEntity } from '../helpers/aml';
import { convertAmlLocationGeoPointEntityToCircleGeoPointEntity } from '../helpers/circles';
import { EntityIdToObjectIdMaps } from '../helpers/entity-id-to-object-id-maps';
import type { CircleGeoPointEntity } from '../types';
import { useCircleFeatureLayer } from './use-circle-feature-layer';

// Draws blue-ish uncertainty radius circle around current AML (RapidSOS) location.
// In its own layer mostly because it's complicated to handle different types of graphic in the same layer.
type AmlUncertaintyRadiusFeatureLayerProps = {
    amlCall?: AmlCallView;
    webMap?: WebMap;
    areRapidSOSAniAliLayersVisible: boolean;
    showAmlCallRadiusAndLedger?: boolean;
    hasClusters?: boolean;
};
export const useAmlUncertaintyRadiusFeatureLayer = ({
    amlCall,
    webMap,
    showAmlCallRadiusAndLedger,
    areRapidSOSAniAliLayersVisible,
    hasClusters = false,
}: AmlUncertaintyRadiusFeatureLayerProps) => {
    const isOnGraphic = useCallback(
        (graphic: Graphic) =>
            graphic.layer?.title === FEATURE_LAYERS_PROPERTIES.AML_UNCERTAINTY_RADIUS.title,
        []
    );

    const entityIdToObjectIdMapCacheRef = useRef<
        typeof EntityIdToObjectIdMaps.AML_UNCERTAINTY_RADIUS
    >(EntityIdToObjectIdMaps.AML_UNCERTAINTY_RADIUS);

    const amlLocationGeoPointEntity = getAmlCallLastAmlLocationGeoPointEntity(amlCall);
    // There will be only one circle to draw in useAmlUncertaintyRadiusFeatureLayer.
    const circleGeoPointEntity = convertAmlLocationGeoPointEntityToCircleGeoPointEntity(
        amlLocationGeoPointEntity
    );
    const circleToDraw: CircleGeoPointEntity[] = circleGeoPointEntity ? [circleGeoPointEntity] : [];

    return useCircleFeatureLayer({
        webMap,
        title: FEATURE_LAYERS_PROPERTIES.AML_UNCERTAINTY_RADIUS.title,
        layerId: FEATURE_LAYERS_PROPERTIES.AML_UNCERTAINTY_RADIUS.layerId,
        layerKey: FEATURE_LAYERS_PROPERTIES.AML_UNCERTAINTY_RADIUS.layerKey,
        circles: circleToDraw,
        color: AML_UNCERTAINTY_CIRCLE_COLOR,
        isOnGraphic: hasClusters ? undefined : isOnGraphic,
        shouldShowRadius: showAmlCallRadiusAndLedger && areRapidSOSAniAliLayersVisible,
        entityIdToObjectIdMapCacheRef,
    });
};
